import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { instantPageService, InstantPageAnalysisResult } from '../lib/instantPageService';
import { 
  ArrowLeft, 
  RefreshCw, 
  Zap, 
  Search,
  Eye,
  Settings,
  Bug,
  Award,
  AlertTriangle,

  BarChart3,
  Clock,
  Target
} from 'lucide-react';
import { LoadingScreen, ErrorScreen } from '../components/common';
import { formatDate } from '../utils';
// Import des utilitaires supprimé car non utilisé
import { calculateAdvancedScoring } from '../utils/advancedScoring';
import PerformanceMetrics from '../components/analysis/PerformanceMetrics';
import MetaInformation from '../components/analysis/MetaInformation';
import TechnicalInfo from '../components/analysis/TechnicalInfo';
import ResourceMetrics from '../components/analysis/ResourceMetrics';
import ResourceErrors from '../components/analysis/ResourceErrors';
import SeoIssues from '../components/analysis/SeoIssues';

const PagePerformanceScore: React.FC = () => {
  const { prospectId, '*': urlPath } = useParams<{ prospectId: string; '*': string }>();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [analysis, setAnalysis] = useState<InstantPageAnalysisResult | null>(null);
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [isAnalyzing, setIsAnalyzing] = useState<boolean>(false);
  const navigate = useNavigate();
  
  // Extraire l'URL depuis le wildcard path
  const url = urlPath || '';
  
  useEffect(() => {
    const fetchAnalysis = async () => {
      if (!prospectId || !url) {
        setError('ID prospect ou URL manquante');
        setLoading(false);
        return;
      }
      
      // 🛡️ Protection contre les appels multiples simultanés
      if (isAnalyzing) {
        return;
      }
      
      setIsAnalyzing(true);
      
      try {
        const result = await instantPageService.analyzeUrl(url, prospectId);
        
        if (result) {
          setAnalysis(result);
        } else {
          setError('Impossible d\'analyser cette page');
        }
      } catch (err) {
        console.error('❌ Erreur lors de l\'analyse:', err);
        setError('Une erreur est survenue lors de l\'analyse');
      } finally {
        setLoading(false);
        setIsAnalyzing(false);
      }
    };
    
    fetchAnalysis();
  }, [prospectId, url, isAnalyzing]);
  
  const handleRefreshAnalysis = async () => {
    if (!prospectId || !url) return;
    
    // 🛡️ Protection contre les appels multiples simultanés
    if (isAnalyzing || refreshing) {
      return;
    }
    
    setRefreshing(true);
    setIsAnalyzing(true);
    setError(null);
    
    try {
      const result = await instantPageService.analyzeUrl(url, prospectId, true);
      
      if (result) {
        setAnalysis(result);
      } else {
        setError('Impossible d\'analyser cette page');
      }
    } catch (err) {
      console.error('❌ Erreur lors de l\'actualisation:', err);
      setError('Une erreur est survenue lors de l\'actualisation');
    } finally {
      setRefreshing(false);
      setIsAnalyzing(false);
    }
  };

  if (loading) {
    return <LoadingScreen message="Analyse de la page en cours..." />;
  }

  if (error || !analysis) {
    return (
      <ErrorScreen
        message={error || 'Aucune donnée disponible'}
        error={error || 'Aucune donnée disponible'}
        onRetry={() => navigate(-1)}
      />
    );
  }

  // Vérifier si la page est "broken" (erreur d'accès)
  const isBroken = (analysis as any).is_broken || analysis.checks?.is_broken;
  const statusCode = (analysis as any).status_code;
  const errorMessage = (analysis as any).error_message;

  // Calcul du scoring avancé seulement si la page n'est pas broken
  const advancedScoring = !isBroken ? calculateAdvancedScoring(analysis) : null;

  const navigationItems = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3, color: 'from-blue-500 to-indigo-500' },
    { id: 'performance', label: 'Performance', icon: Zap, color: 'from-green-500 to-emerald-500' },
    { id: 'seo', label: 'SEO & Contenu', icon: Search, color: 'from-orange-500 to-red-500' },
    { id: 'technical', label: 'Technique', icon: Settings, color: 'from-indigo-500 to-purple-500' },
    { id: 'issues', label: 'Problèmes', icon: Bug, color: 'from-red-500 to-pink-500' }
  ];

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 90) return 'from-green-500 to-emerald-500';
    if (score >= 70) return 'from-blue-500 to-indigo-500';
    if (score >= 50) return 'from-orange-500 to-yellow-500';
    return 'from-red-500 to-pink-500';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <img src="/images/smart_impact_logo.jpg" alt="Smart Impact" className="h-12" />
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft size={16} className="mr-2" />
                Retour
              </button>
              <button
                onClick={handleRefreshAnalysis}
                disabled={refreshing}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all transform hover:scale-105 disabled:opacity-50"
              >
                <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                {refreshing ? 'Actualisation...' : 'Actualiser'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-8">
        <div className="absolute inset-0 z-0 opacity-30">
          <div className="absolute right-0 top-0 -translate-y-1/4 translate-x-1/4 transform rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 blur-3xl filter opacity-70" style={{ width: '40rem', height: '25rem' }}></div>
          <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 transform rounded-full bg-gradient-to-br from-purple-400 to-fuchsia-500 blur-3xl filter opacity-70" style={{ width: '40rem', height: '25rem' }}></div>
        </div>

        <div className="container relative z-10 mx-auto px-4">
          <div className="mx-auto max-w-5xl text-center">
            <div className="inline-flex items-center justify-center rounded-full bg-blue-100 px-4 py-1.5 text-sm font-medium text-blue-800 mb-6 animate-fadeSlideUp">
              <Award size={16} className="mr-2" />
              Analyse de Performance Détaillée
            </div>
            
            <h1 className="mb-6 text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl animate-fadeSlideUp" style={{animationDelay: '100ms'}}>
              <span className="relative inline-block">
                <span className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-r from-blue-400 to-indigo-500 opacity-30"></span>
                <span className="relative">Rapport Technique</span>
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                {url}
              </span>
            </h1>

            {/* Score global */}
            {advancedScoring && (
              <div className="flex justify-center mb-8 animate-fadeSlideUp" style={{animationDelay: '200ms'}}>
                <div className="relative">
                  <div className={`w-32 h-32 rounded-full bg-gradient-to-br ${getScoreBgColor(advancedScoring.global.score)} p-1 shadow-2xl`}>
                    <div className="w-full h-full bg-white rounded-full flex items-center justify-center">
                      <div className="text-center">
                        <div className={`text-3xl font-bold ${getScoreColor(advancedScoring.global.score)}`}>
                          {advancedScoring.global.score}
                        </div>
                        <div className="text-xs text-gray-500">Score Global</div>
                      </div>
                    </div>
                  </div>
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                    <span className={`inline-block px-3 py-1 text-xs font-bold text-white rounded-full bg-gradient-to-r ${getScoreBgColor(advancedScoring.global.score)}`}>
                      Grade {advancedScoring.global.grade}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Métriques rapides */}
            {advancedScoring && (
              <div className="flex flex-wrap justify-center gap-3 mb-8 animate-fadeSlideUp" style={{animationDelay: '300ms'}}>
                <div className="inline-flex items-center rounded-full bg-green-50 px-4 py-1.5 text-sm font-medium text-green-700 shadow-sm">
                  <Zap size={14} className="mr-2" />
                  Performance {Math.round(advancedScoring.categories.performance.score)}%
                </div>
                <div className="inline-flex items-center rounded-full bg-blue-50 px-4 py-1.5 text-sm font-medium text-blue-700 shadow-sm">
                  <Search size={14} className="mr-2" />
                  SEO {Math.round(advancedScoring.categories.seo.score)}%
                </div>
                <div className="inline-flex items-center rounded-full bg-purple-50 px-4 py-1.5 text-sm font-medium text-purple-700 shadow-sm">
                  <Eye size={14} className="mr-2" />
                  UX {Math.round(advancedScoring.categories.ux.score)}%
                </div>
                <div className="inline-flex items-center rounded-full bg-indigo-50 px-4 py-1.5 text-sm font-medium text-indigo-700 shadow-sm">
                  <Settings size={14} className="mr-2" />
                  Technique {Math.round(advancedScoring.categories.technical.score)}%
                </div>
              </div>
            )}

            {/* Résumé des problèmes */}
            <div className="flex justify-center gap-6 animate-fadeSlideUp" style={{animationDelay: '400ms'}}>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{advancedScoring.totalIssues.critical}</div>
                <div className="text-sm text-gray-600">Problèmes Critiques</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{advancedScoring.totalIssues.warnings}</div>
                <div className="text-sm text-gray-600">Avertissements</div>
              </div>
              <div className="text-center">
                <div className="text-sm text-gray-500">Analysé le</div>
                <div className="text-sm font-medium text-gray-700">{formatDate(analysis.analysis_date)}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <div className="container mx-auto px-4 mb-8">
        <div className="mx-auto max-w-5xl">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
              {navigationItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveSection(item.id)}
                    className={`p-4 text-center transition-all duration-300 hover:bg-gray-50 ${
                      isActive ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-b-2 border-blue-500' : ''
                    }`}
                    style={{animationDelay: `${index * 50}ms`}}
                  >
                    <div className={`w-8 h-8 mx-auto mb-2 rounded-lg bg-gradient-to-br ${item.color} p-1.5 text-white`}>
                      <Icon size={20} />
                    </div>
                    <div className={`text-xs font-medium ${isActive ? 'text-blue-700' : 'text-gray-600'}`}>
                      {item.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 pb-20">
        <div className="mx-auto max-w-5xl">
          {/* Vue d'ensemble */}
          {activeSection === 'overview' && (
            <div className="space-y-8 animate-fadeSlideUp">
              {/* Scores par catégorie */}
              {advancedScoring && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {Object.entries(advancedScoring.categories).map(([key, category], index) => {
                  const icons = {
                    performance: Zap,
                    seo: Search,
                    ux: Eye,
                    technical: Settings
                  };
                  const colors = {
                    performance: 'from-green-500 to-emerald-500',
                    seo: 'from-blue-500 to-indigo-500',
                    ux: 'from-purple-500 to-fuchsia-500',
                    technical: 'from-indigo-500 to-purple-500'
                  };
                  const Icon = icons[key as keyof typeof icons];
                  
                  return (
                    <div key={key} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-md" style={{animationDelay: `${index * 100}ms`}}>
                      <div className={`p-1 bg-gradient-to-r ${colors[key as keyof typeof colors]}`}></div>
                      <div className="p-6 text-center">
                        <div className={`w-12 h-12 mx-auto mb-4 rounded-lg bg-gradient-to-br ${colors[key as keyof typeof colors]} p-2.5 text-white`}>
                          <Icon size={24} />
                        </div>
                        <div className="text-3xl font-bold text-gray-900 mb-1">
                          {Math.round(category.score)}
                        </div>
                        <div className="text-sm text-gray-600 mb-2 capitalize">
                          {key === 'ux' ? 'UX Design' : key}
                        </div>
                        <div className={`inline-block px-2 py-1 text-xs font-bold text-white rounded-full bg-gradient-to-r ${getScoreBgColor(category.score)}`}>
                          Grade {category.grade}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          {category.issues && category.issues.length} problème{category.issues && category.issues.length > 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>
                  );
                  })}
                </div>
              )}

              {/* Recommandations prioritaires */}
              {advancedScoring && advancedScoring.priorities.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-1 bg-gradient-to-r from-purple-500 to-fuchsia-500"></div>
                  <div className="p-6">
                    <div className="flex items-center mb-6">
                      <div className="p-2 rounded-lg bg-purple-50 text-purple-700 mr-4">
                        <Target size={20} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Actions Prioritaires</h3>
                        <p className="text-gray-600">Recommandations à fort impact pour améliorer votre score</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      {advancedScoring.priorities.slice(0, 3).map((recommendation, index) => (
                        <div key={recommendation.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-center mb-3">
                            <div className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">
                              {index + 1}
                            </div>
                            <h4 className="font-semibold text-gray-900 text-sm">{recommendation.title}</h4>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{recommendation.description}</p>
                          <div className="flex gap-2 mb-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              recommendation.impact === 'high' ? 'bg-red-100 text-red-700' :
                              recommendation.impact === 'medium' ? 'bg-orange-100 text-orange-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              Impact {recommendation.impact}
                            </span>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${
                              recommendation.effort === 'high' ? 'border-red-200 text-red-700' :
                              recommendation.effort === 'medium' ? 'border-orange-200 text-orange-700' :
                              'border-green-200 text-green-700'
                            }`}>
                              Effort {recommendation.effort}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500">
                            <div className="flex items-center mb-1">
                              <Clock size={12} className="mr-1" />
                              {recommendation.timeEstimate}
                            </div>
                            <div className="font-medium text-purple-600">
                              {recommendation.businessImpact}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Performance */}
          {activeSection === 'performance' && (
            <div className="space-y-8 animate-fadeSlideUp">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-green-500 to-emerald-500"></div>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 rounded-lg bg-green-50 text-green-700 mr-4">
                      <Zap size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Métriques de Performance</h3>
                      <p className="text-gray-600">Analyse détaillée des temps de chargement et Core Web Vitals</p>
                    </div>
                  </div>
                  {analysis.page_timing && <PerformanceMetrics timing={analysis.page_timing} />}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-blue-500 to-indigo-500"></div>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 rounded-lg bg-blue-50 text-blue-700 mr-4">
                      <BarChart3 size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Ressources de la Page</h3>
                      <p className="text-gray-600">Analyse des ressources chargées (scripts, images, CSS)</p>
                    </div>
                  </div>
                  {analysis.meta && <ResourceMetrics meta={analysis.meta} />}
                </div>
              </div>
            </div>
          )}

          {/* SEO & Contenu */}
          {activeSection === 'seo' && (
            <div className="space-y-8 animate-fadeSlideUp">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-orange-500 to-red-500"></div>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 rounded-lg bg-orange-50 text-orange-700 mr-4">
                      <Search size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Informations Meta & Contenu</h3>
                      <p className="text-gray-600">Analyse du contenu et des métadonnées pour le référencement</p>
                    </div>
                  </div>
                  {analysis.meta && <MetaInformation meta={analysis.meta} />}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-yellow-500 to-orange-500"></div>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 rounded-lg bg-yellow-50 text-yellow-700 mr-4">
                      <AlertTriangle size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Problèmes SEO Détectés</h3>
                      <p className="text-gray-600">Résumé des problèmes détectés lors de l'analyse SEO</p>
                    </div>
                  </div>
                  {analysis.seo_issues && <SeoIssues seo_issues={analysis.seo_issues} />}
                </div>
              </div>
            </div>
          )}

          {/* Technique */}
          {activeSection === 'technical' && (
            <div className="animate-fadeSlideUp">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 rounded-lg bg-indigo-50 text-indigo-700 mr-4">
                      <Settings size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Informations Techniques</h3>
                      <p className="text-gray-600">Détails techniques sur la configuration et les performances du serveur</p>
                    </div>
                  </div>
                  {analysis.technical_info && <TechnicalInfo technical_info={analysis.technical_info} />}
                </div>
              </div>
            </div>
          )}

          {/* Problèmes */}
          {activeSection === 'issues' && (
            <div className="animate-fadeSlideUp">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-1 bg-gradient-to-r from-red-500 to-pink-500"></div>
                <div className="p-6">
                  <div className="flex items-center mb-6">
                    <div className="p-2 rounded-lg bg-red-50 text-red-700 mr-4">
                      <Bug size={20} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">Erreurs et Avertissements</h3>
                      <p className="text-gray-600">Erreurs et avertissements détectés dans le code HTML/CSS</p>
                    </div>
                  </div>
                  {analysis.resource_errors && <ResourceErrors resource_errors={analysis.resource_errors} />}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <style>
        {`
          @keyframes fadeSlideUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeSlideUp {
            animation: fadeSlideUp 0.6s ease-out forwards;
          }
        `}
      </style>
    </div>
  );
};

export default PagePerformanceScore;
